import api from 'api';
import { userTypes } from 'constants/user';
import { updateWorkflowAsyncSuccess } from 'actions/workflows';
import { updateUserBrandsSuccess } from 'actions/user-brands';
import { updateUserSuccess } from 'actions/user';

import { Workflows } from 'records/Workflow';
import { buildQueryParams } from 'helpers/util';
import { PAGE_SIZE } from 'constants/account';
import { hasInactiveAccounts } from 'helpers/account';

export const ACCOUNT_RESET_STATE = 'ACCOUNT_RESET_STATE';
export const ACCOUNT_ASYNC_ACTION_ERROR = 'ACCOUNT_ASYNC_ACTION_ERROR';
export const ACCOUNT_ASYNC_ACTION_START = 'ACCOUNT_ASYNC_ACTION_START';
export const ACCOUNT_ASYNC_SET_PAGE = 'ACCOUNT_ASYNC_SET_PAGE';
export const ACCOUNT_LOAD_NEW_PAGE_START = 'ACCOUNT_LOAD_NEW_PAGE_START';
export const ACCOUNT_LOAD_NEW_PAGE_SUCCESS = 'ACCOUNT_LOAD_NEW_PAGE_SUCCESS';
export const ACCOUNT_SET_SELECTED_STATE = 'ACCOUNT_SET_SELECTED_STATE';
export const UPDATE_ACCOUNT_SELECTION_ON_CHECK = 'UPDATE_ACCOUNT_SELECTION_ON_CHECK';

/**
 * Set internal associate customers
 * @param {Array} accounts assigned accounts
 * @param {Number} count count of assigned accounts
 * @param {Number} nextOffset next offset number
 * @param {String} nextTerritory next territory string
 * @returns action
 */
function updateInternalAccountSuccess(accounts, count, nextOffset = 0, nextTerritory = null) {
  const hasMore = count > PAGE_SIZE;
  const removed = (hasMore ? PAGE_SIZE : count) - accounts.length;

  return {
    type: ACCOUNT_ASYNC_SET_PAGE,
    payload: {
      offset: nextOffset,
      territories: nextTerritory || null,
      accounts,
      count: count - removed,
      hasMore,
      hasInactive: hasInactiveAccounts(accounts),
    }
  };
}

/**
 * Set external associate customers
 * @param {Array} accounts customer account list
 * @param {Number} count data count 
 * @returns action
 */
function updateExternalAccountSuccess(accounts, count) {
  return {
    type: ACCOUNT_ASYNC_SET_PAGE,
    payload: {
      accounts,
      count,
      hasInactive: hasInactiveAccounts(accounts),
    }
  };
}

export function resetAccountState() {
  return {
    type: ACCOUNT_RESET_STATE,
  };
}

function accountAsyncStart() {
  return {
    type: ACCOUNT_ASYNC_ACTION_START,
  };
}

function accountAsyncError(error) {
  return {
    type: ACCOUNT_ASYNC_ACTION_ERROR,
    error,
  };
}

const accountPageLoadStart = () => ({
  type: ACCOUNT_LOAD_NEW_PAGE_START,
});

/**
 * Append next customer page
 * @param {Array} accounts list of accounts
 * @param {Number} count no of accounts in coming pages
 * @param {Number} nextOffset next page index to start
 * @param {String} nextTerritory next territory string to include
 * @returns 
 */
const accountNewPageLoadSuccess = (accounts, count, nextOffset, nextTerritory) => ({
  type: ACCOUNT_LOAD_NEW_PAGE_SUCCESS,
  payload: {
    offset: nextOffset,
    territories: nextTerritory || null,
    accounts,
    hasMore: count > PAGE_SIZE,
    hasInactive: hasInactiveAccounts(accounts),
  }
});

/**
 Fetch External account assignment first page
 * @param {Boolean} includeInactives include inactive accounts with the response
 * @returns response
 */
export function fetchExternalAssignments(includeInactives) {
  return (dispatch) => {
    const queryParams = buildQueryParams({ includeInactive: includeInactives });

    dispatch(accountAsyncStart());
    return api.request(`account/${userTypes.EXTERNAL}/assignments`, 'get', null, null, null, queryParams)
      .then((data) => {
        if (data) {
          dispatch(updateExternalAccountSuccess(data.accounts, data.count));
        }
        return data;
      })
      .catch((err) => {
        if (err && err.message) {
          dispatch(accountAsyncError(err.message));
        } else {
          dispatch(accountAsyncError(err));
        }
        return undefined;
      });
  };
}

/**
 * Fetch internal account assignment first page
 * @param {String} opcoId opco Id
 * @param {String} adId assigned federated Id: accounts will be fetched under this user's territories
 * @param {String} customerKey search term
 * @param {Boolean} includeInactives include inactive accounts with the response
 * @param {toChangeAccount} toChangeAccount fetching internal accounts to change account
 * @returns response
 */
export function fetchInternalAssignments(options) {
  const {
    opcoId,
    adId,
    customerKey,
    includeInactives,
    detailed = false,
    toChangeAccount = false,
    restaurantSolutionTier,
    businessType,
    cuisineType,
    pageSize
  } = options;

  return (dispatch) => {
    const queryParams = buildQueryParams({ opcoId, adId, customerKey, includeInactive: includeInactives, detailed, 
      toChangeAccount, restaurantSolutionTier, businessType, cuisineType, pageSize });
    
    dispatch(accountAsyncStart());
    return api.request(`account/internal/assignments`, 'get', null, null, null, queryParams)
      .then(data => {
        const next = data.next;
        dispatch(updateInternalAccountSuccess(data.accounts, data.count, next && next.offset, next && next.territories));
        return data;
      })
      .catch(err => {
        dispatch(accountAsyncError(err && err.message));
        throw err;
      });
  };
}

/**
 * fetch next page for the given offset
 * @param {String} opcoId opco id
 * @param {String} adId federation id
 * @param {String} customerKey search term
 * @param {Number} offset required page offset
 * @param {String} territories territory string
 * @returns 
 */
export function fetchInternalNextPage(options) {

  const {
    opcoId,
    adId,
    customerKey,
    offset,
    territories,
    restaurantSolutionTier,
    businessType,
    cuisineType
  } = options;

  return (dispatch) => {
    if (!offset || offset <= 0) {
      const errorMsg = "offset is mandatory to fetch next page";
      dispatch(accountAsyncError(errorMsg));
      return Promise.reject(new Error(errorMsg));
    }

    const queryParams = buildQueryParams({ opcoId, adId, customerKey, offset, territories, restaurantSolutionTier, businessType, cuisineType });
    dispatch(accountPageLoadStart());
    return api.request(`account/internal/assignments`, 'get', null, null, null, queryParams)
      .then(data => {
        const next = data && data.next;
        dispatch(accountNewPageLoadSuccess(data && data.accounts, data && data.count, next && next.offset, next && next.territories));
        return data;
      })
      .catch(err => {
        dispatch(accountAsyncError(err && err.message));
        throw err;
      });
  };
}

/**
 * switch between assigned accounts
 * @param {Object} switchUser 
 * @returns worksflow data format with brand details
 */
export function switchAccount(switchUser) {
  return (dispatch) => {
    return api.request('user/switch', 'post', switchUser)
      .then((data) => {
        dispatch(updateUserSuccess(data.user));
        dispatch(updateWorkflowAsyncSuccess(data.workflow));
        dispatch(updateUserBrandsSuccess(data.brands));
        return Workflows(data.workflow);
      })
      .catch(err => {
        dispatch(accountAsyncError(err && err.message));
        throw err;
      });
  };
}

/**
 * set given account as selected
 * @param {String} accountId account ID. eg: '067-000018'
 * @param {Boolean} isSelected account ID. eg: '067-000018'
 */
export const setSelectedStateByAccountId = (accountId, isSelected) => ({
  type: ACCOUNT_SET_SELECTED_STATE,
  payload: { accountId, isSelected }
});

export const setAccountSelection = (account) => {
  return {
    type: UPDATE_ACCOUNT_SELECTION_ON_CHECK,
    payload: {
      account
    }
  };
};
